import { Component, inject } from '@angular/core';

import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { CostAnalysisDisplayComponent } from '../cost-analysis-display/cost-analysis-display.component';
import { MetaCostAnalysis } from '../../../../../generated.commonjs/cost_analysis_pb';
import { getTaskUrl } from '../../../../shared/task-navigation.util';

export interface CostDetailsDialogData {
  costAnalysis: MetaCostAnalysis.AsObject;
  taskId?: string;
  taskTitle?: string;
  projectId?: string;
}

/** Modal dialog presenting a detailed cost-analysis breakdown for a specific task, including per-model token counts and costs. */
@Component({
  selector: 'app-cost-details-dialog',
  imports: [MatDialogModule, MatButtonModule, MatIconModule, RouterModule, CostAnalysisDisplayComponent],
  template: `
    <div class="cost-details-dialog">
      <div mat-dialog-title class="dialog-title">
        <mat-icon>analytics</mat-icon>
        <span>Cost Analysis Details</span>
        <button mat-icon-button mat-dialog-close class="close-button" matTooltip="Close">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div mat-dialog-content class="dialog-content">
        @if (data.taskTitle || data.taskId) {
          <div class="task-info">
            @if (data.taskTitle) {
              <h3>{{ data.taskTitle }}</h3>
            }
            @if (data.taskId) {
              <p class="task-id">
                Task ID:
                @if (data.projectId) {
                  <a [routerLink]="getTaskLink()" (click)="dialogRef.close()" class="task-id-link">
                    <code>{{ data.taskId }}</code>
                  </a>
                } @else {
                  <code>{{ data.taskId }}</code>
                }
              </p>
            }
          </div>
        }

        <app-cost-analysis-display
          [costAnalysis]="data.costAnalysis"
          displayMode="detailed"
          [showBreakdown]="true"
        ></app-cost-analysis-display>
      </div>

      <div mat-dialog-actions align="end">
        <button mat-button mat-dialog-close>Close</button>
      </div>
    </div>
  `,
  styles: [
    `
      .cost-details-dialog {
        min-width: 500px;
        max-width: 700px;
      }

      .dialog-title {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 20px 24px 16px;
        margin: 0;
        border-bottom: 1px solid #e0e0e0;

        mat-icon {
          color: #1976d2;
        }

        span {
          flex: 1;
          font-size: 20px;
          font-weight: 500;
        }

        .close-button {
          margin-left: auto;
        }
      }

      .dialog-content {
        padding: 24px;
        max-height: 600px;
        overflow-y: auto;
      }

      .task-info {
        margin-bottom: 24px;
        padding-bottom: 16px;
        border-bottom: 1px solid #f0f0f0;

        h3 {
          margin: 0 0 8px 0;
          color: #333;
          font-size: 18px;
          font-weight: 500;
        }

        .task-id {
          margin: 0;
          color: #666;
          font-size: 14px;

          code {
            background: #f5f5f5;
            padding: 2px 6px;
            border-radius: 4px;
            font-family: 'Roboto Mono', monospace;
          }

          .task-id-link {
            color: #1976d2;
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }

            code {
              color: inherit;
            }
          }
        }
      }

      ::ng-deep .cost-details-dialog {
        .mat-mdc-dialog-title {
          padding: 0;
        }

        .mat-mdc-dialog-content {
          padding: 0;
        }

        .mat-mdc-dialog-actions {
          padding: 16px 24px 20px;
          margin: 0;
          border-top: 1px solid #e0e0e0;
        }
      }

      /* Consistent padding across all build configurations - targets the panelClass */
      ::ng-deep .cost-details-dialog-panel {
        .mat-mdc-dialog-container {
          padding: 0 !important;
        }

        .mat-mdc-dialog-content {
          padding: 24px !important;
          margin: 0 !important;
        }

        .mat-mdc-dialog-title {
          padding: 24px 24px 16px 24px !important;
          margin: 0 !important;
        }

        .mat-mdc-dialog-actions {
          padding: 16px 24px 20px 24px !important;
          margin: 0 !important;
          border-top: 1px solid #e0e0e0;
        }
      }
    `,
  ],
})
export class CostDetailsDialogComponent {
  dialogRef = inject<MatDialogRef<CostDetailsDialogComponent>>(MatDialogRef);
  data = inject<CostDetailsDialogData>(MAT_DIALOG_DATA);

  getTaskLink(): string[] {
    return getTaskUrl(this.data.projectId!, this.data.taskId);
  }
}
